'use client';
import React, { useEffect, lazy, Suspense } from 'react';
import MobileLayout from '@/components/MobileLayout';
import QuickActions from './QuickActions';
import { useGlobalStore } from '@/store/useGlobalStore';
import { motion } from 'framer-motion';
import { useRouter } from 'next/navigation';
import { useWebSocket } from '@/hooks/useWebSocket';
import { useDashboardStreams } from '@/hooks/useDashboardStreams';
import appConfig from '@/config/appConfig';
import { useTranslation } from 'react-i18next';

// Lazy load heavy below-the-fold components
const BannerCarousel = lazy(() => import('./BannerCarousel'));
const TradingViewTickers = lazy(() => import('./TradingViewTickers'));
const NewsSection = lazy(() => import('./NewsSection'));

function SectionSkeleton() {
  return (
    <div className="px-4 mb-4">
      <div className="h-32 rounded-2xl animate-pulse" style={{ background: 'var(--muted)' }} />
    </div>
  );
}

/** Compact live price row rendered from WS tickers */
function LiveTickerBar({ tickers }: { tickers: Record<string, { symbol: string; bid: number; ask: number; changePercent: number }> }) {
  const entries = Object.values(tickers);
  if (entries.length === 0) return null;
  return (
    <div
      className="mx-4 mb-3 rounded-xl px-3 py-2 flex gap-3 overflow-x-auto scrollbar-hide"
      style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
    >
      {entries.map((t) => {
        const bid = Number(t.bid);
        const changePercent = Number(t.changePercent);
        const isPositive = (Number.isFinite(changePercent) ? changePercent : 0) >= 0;
        return (
          <div key={t.symbol} className="flex-shrink-0 flex flex-col items-center gap-0.5 min-w-[64px]">
            <span className="text-[9px] font-bold text-muted-foreground uppercase tracking-wide">{t.symbol}</span>
            <span className="text-xs font-mono font-semibold text-foreground">
              {(Number.isFinite(bid) ? bid : 0).toFixed(5)}
            </span>
            <span
              className="text-[9px] font-semibold"
              style={{ color: isPositive ? 'var(--positive)' : 'var(--negative)' }}
            >
              {isPositive ? '+' : ''}{(Number.isFinite(changePercent) ? changePercent : 0).toFixed(2)}%
            </span>
          </div>
        );
      })}
    </div>
  );
}

export default function DashboardScreen() {
  const { isAuthenticated, isBootstrapping, user } = useGlobalStore();
  const { t } = useTranslation();
  const router = useRouter();
  const [mounted, setMounted] = React.useState(false);

  // Ensure WS is connected (auth-aware)
  useWebSocket();

  // Subscribe to live streams: tickers, balances, notifications
  const { tickers } = useDashboardStreams();

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (mounted && !isBootstrapping && !isAuthenticated) {
      router?.push(appConfig.multiple ? '/company' : '/login');
    }
  }, [mounted, isAuthenticated, isBootstrapping, router]);

  if (!mounted || isBootstrapping) {
    return (
      <div
        className="flex items-center justify-center min-h-screen"
        style={{ background: 'var(--background)' }}
      >
        <div
          className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin"
          style={{ borderColor: 'var(--primary)', borderTopColor: 'transparent' }}
        />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  const hour = new Date().getHours();
  const greeting = hour < 12 ? t('goodMorning') : hour < 18 ? t('goodAfternoon') : t('goodEvening');

  return (
    <MobileLayout>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        className="pb-4"
      >
        {/* Welcome */}
        <div className="px-4 pt-4 pb-2">
          <p className="text-xs text-muted-foreground">{greeting} 👋</p>
          <h2 className="text-xl font-bold text-foreground">{user?.name?.split(' ')?.[0] || 'Trader'}</h2>
        </div>

        {/* Live WS price tickers (shown only when WS data arrives) */}
        <LiveTickerBar tickers={tickers} />

        {/* Quick Actions */}
        <QuickActions />

        {/* Banner Carousel — lazy loaded */}
        <Suspense fallback={<SectionSkeleton />}>
          <BannerCarousel />
        </Suspense>

        {/* TradingView Tickers marquee — lazy loaded */}
        <Suspense fallback={<SectionSkeleton />}>
          <TradingViewTickers />
        </Suspense>

        {/* Market News with infinite scroll — lazy loaded */}
        <Suspense fallback={<SectionSkeleton />}>
          <NewsSection />
        </Suspense>
      </motion.div>
    </MobileLayout>
  );
}